import ShieldIcon from "@mui/icons-material/Shield";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchCompanyDetailbyCIN,
  fetchCompanyDetailbyGst,
  getIndustries,
  getLocationByPincode,
  getSectors,
} from "../../../API/API";
import {
  selectFormData,
  setIsDocFetched,
  updateFormField,
} from "../../../ReduxStore/UserSlice";
import { FULL_TERMS_CONTENT } from "../../../utils/termsContent";
import TermsDialog from "../../common/TermsDialog";
import {
  handleOpenPrivacyPolicy,
  handleOpenTermsOfService,
  showToast,
} from "../../common/Util/Util";
import { basicDetailsValidation } from "../../common/ValidationRules/validationRules";

const BasicDetailsForm = React.forwardRef((props, ref) => {
  const {
    control,
    formState: { errors },
    trigger,
    setValue,
    setError,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      gstin: "",
      nameAsPerGST: "",
      addr: "",
      confirmations: false,
      industry: "",
      sector: "",
      state: "",
      city: "",
      zipCode: "",
    },
  });

  const dispatch = useDispatch();
  const formData = useSelector(selectFormData);
  const [checkGstChange, setcheckGstChange] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [showGstinHelp, setShowGstinHelp] = useState(false);
  const [termsDialog, setTermsDialog] = useState({
    open: false,
    title: "",
    content: "",
    highlightSection: "",
  });

  React.useImperativeHandle(ref, () => ({
    validateForm: async () => {
      // run built-in validations
      const result = await trigger();
      if (!formData?.nameAsPerGST) {
        setError("gstin", {
          type: "manual",
          message: "Invalid GSTIN. Could not fetch company details.",
        });
        return false;
      }

      if (!formData?.confirmations) {
        return false;
      }

      return result;
    },
  }));
  const handleFetchCompanydetail = (CompanyCIN) => {
    fetchCompanyDetailbyCIN(CompanyCIN)
      .then((res) => {
        if (res?.statusCode === 200 && res?.result !== null) {
          if (res?.result?.Director?.length > 0) {
            dispatch(
              updateFormField({
                field: "directorListJson",
                value: JSON.stringify(res?.result?.Director),
              })
            );
          } else {
            dispatch(
              updateFormField({
                field: "directorListJson",
                value: "",
              })
            );
          }
          dispatch(
            updateFormField({
              field: "entityType",
              value: "company",
            })
          );
        } else {
          dispatch(
            updateFormField({
              field: "entityType",
              value: "",
            })
          );
          dispatch(
            updateFormField({
              field: "directorListJson",
              value: null,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFormField({ field: name, value: value }));
    setcheckGstChange(true);
  };

  useEffect(() => {
    setValue("gstin", formData.gstin);
    setValue("addr", formData.addr);
    setValue("nameAsPerGST", formData.nameAsPerGST);
    setValue("confirmations", formData.confirmations);
    setValue("industry", formData.industry);
    setValue("sector", formData.sector);
    setValue("otherSector", formData.otherSector);
    setValue("state", formData.state);
    setValue("city", formData.city);
    setValue("zipCode", formData.zipCode);
  }, []);

  useEffect(() => {
    getIndustries()
      .then((res) => {
        if (res?.statusCode === 200 && Array.isArray(res?.result?.industry)) {
          setIndustries(res.result.industry);
        } else {
          toast.error("Industries data is not in expected format:", res);
          setIndustries([]);
        }
      })
      .catch((error) => {
        toast.error("Error fetching industries:", error);
        setIndustries([]);
      });
  }, []);

  useEffect(() => {
    if (formData?.industry) {
      getSectors(formData?.industry)
        .then((res) => {
          if (res?.statusCode === 200 && Array.isArray(res?.result?.sector)) {
            setSectors(res?.result?.sector);
          } else {
            toast.error("Sectors data is not in expected format:", res);
            setSectors([]);
          }
        })
        .catch((error) => {
          toast.error("Error fetching sectors:", error);
          setSectors([]);
        });
    } else {
      setSectors([]);
    }
  }, [formData.industry]);

  const handleFetchGstdetail = (gstIn) => {
    dispatch(updateFormField({ field: "datafetchedFromportal", value: false }));
    dispatch(updateFormField({ field: "directorListJson", value: null }));
    fetchCompanyDetailbyGst(gstIn)
      .then((res) => {
        if (res?.statusCode === 200) {
          if (res?.result !== null) {
            const company = res?.result;
            dispatch(
              updateFormField({
                field: "nameAsPerGST",
                value: company?.legalName,
              })
            );
            dispatch(
              updateFormField({ field: "addr", value: company?.address })
            );
            dispatch(setIsDocFetched(false));
            if (company?.cinNumber) {
              dispatch(
                updateFormField({
                  field: "companyCin",
                  value: company?.cinNumber,
                })
              );
              handleFetchCompanydetail(company?.cinNumber);
            } else {
              dispatch(
                updateFormField({
                  field: "directorListJson",
                  value: null,
                })
              );
              setShowCompanySelector(true);
            }
          } else {
            dispatch(updateFormField({ field: "entityType", value: "" }));
            dispatch(updateFormField({ field: "companyCin", value: "" }));
            dispatch(updateFormField({ field: "nameAsPerGST", value: "" }));
            dispatch(updateFormField({ field: "addr", value: "" }));
            showToast(
              "Invalid GSTIN. Could not fetch company details.",
              "error"
            );
          }
        } else {
          showToast(res?.message, "error");
        }
      })
      .catch((error) => {
        dispatch(setIsDocFetched(false));
        dispatch(updateFormField({ field: "authorized", value: false }));
        dispatch(updateFormField({ field: "entityType", value: "" }));
        dispatch(updateFormField({ field: "companyCin", value: "" }));
        dispatch(updateFormField({ field: "nameAsPerGST", value: "" }));
        dispatch(updateFormField({ field: "addr", value: "" }));
        showToast("Invalid GSTIN. Could not fetch company details.", "error");
        console.error("Error fetching company details:", error);
      });
  };
  useEffect(() => {
    if (formData?.gstin?.length === 15 && !errors.gstin && checkGstChange) {
      setTimeout(() => {
        handleFetchGstdetail(formData?.gstin);
      }, 300);
    }
  }, [formData?.gstin, errors.gstin, checkGstChange]);

  useEffect(() => {
    const zipCode = (formData?.zipCode || "").toString().trim();

    const clearStateAndCity = () => {
      if ((formData?.state || "") !== "") {
        dispatch(updateFormField({ field: "state", value: "" }));
        setValue("state", "", { shouldValidate: true, shouldDirty: true });
      }
      if ((formData?.city || "") !== "") {
        dispatch(updateFormField({ field: "city", value: "" }));
        setValue("city", "", { shouldValidate: true, shouldDirty: true });
      }
    };

    const updateField = (field, newValue) => {
      const currentValue = formData?.[field] || "";
      if (newValue && newValue !== currentValue) {
        dispatch(updateFormField({ field, value: newValue }));
        setValue(field, newValue, { shouldValidate: true, shouldDirty: true });
      }
    };

    if (!/^\d{6}$/.test(zipCode)) {
      clearStateAndCity();
      return;
    }

    getLocationByPincode(zipCode)
      .then((response) => {
        const data = (Array.isArray(response) ? response[0] : response) || {};
        const offices = Array.isArray(data?.PostOffice)
          ? data.PostOffice
          : Array.isArray(data?.postOffice)
            ? data.postOffice
            : [];
        if (data?.Status !== "Success" || offices.length === 0) {
          toast.error(
            "No records found for this pincode. Please enter a valid pincode."
          );
          setValue("zipCode", "", { shouldValidate: true, shouldDirty: true });
          clearStateAndCity();
          return;
        }

        const [office] = offices;
        updateField("state", office?.State || "");
        updateField("city", office?.District || "");
      })
      .catch(() => {
        toast.error(
          "Error fetching location by pincode. Please enter correct pincode."
        );
        setValue("zipCode", "", { shouldValidate: true, shouldDirty: true });
        clearStateAndCity();
      });
  }, [formData?.zipCode, dispatch, setValue]);

  const handleOpenTerms = (type) => {
    setTermsDialog({
      open: true,
      title: "Terms and Conditions",
      content: FULL_TERMS_CONTENT,
      highlightSection: type,
    });
  };

  const handleCloseTerms = () => {
    setTermsDialog({
      open: false,
      title: "",
      content: "",
      highlightSection: "",
    });
  };

  const handleAgreeTerms = async () => {
    setValue("confirmations", true, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    handleChange({
      target: {
        name: "confirmations",
        value: true,
      },
    });

    await trigger("confirmations");
  };

  return (
    <Box component="form">
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Step 1/5
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 2 }}>
          Enter your 15-digit GSTIN number. This will help us fetch your entity
          details automatically.{" "}
          <Box component="span" sx={{ fontWeight: 400 }}>
            Don't have a GSTIN number?{" "}
            <Link
              component="button"
              onClick={() => setShowGstinHelp((prev) => !prev)}
              sx={{ fontWeight: 600, textTransform: "none" }}
            >
              See more
            </Link>
          </Box>
        </Typography>
        {showGstinHelp && (
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              p: 2,
              backgroundColor: "#fafafa",
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              • You are currently in the Entity Borrower loan application
              journey.
            </Typography>
            <Typography variant="body2">
              • To proceed with a personal/Home loan application, please select
              or create your profile as an Individual Borrower and continue your
              application from there.
            </Typography>
          </Box>
        )}
        <Controller
          name="gstin"
          control={control}
          rules={basicDetailsValidation.gstin}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="GSTIN"
              required
              placeholder="15 digit GSTIN number"
              error={!!errors.gstin}
              slotProps={{
                htmlInput: { maxLength: 15 },
                inputLabel: { shrink: true },
              }}
              helperText={errors.gstin?.message}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              value={formData?.gstin}
              onChange={(e) => {
                e.target.value.replace(/^\s+/, "");
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />

        <Controller
          name="industry"
          control={control}
          rules={basicDetailsValidation.industry}
          render={({ field }) => (
            <TextField
              {...field}
              select
              fullWidth
              label="Industry"
              required
              error={!!errors.industry}
              helperText={errors.industry?.message}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              value={formData?.industry || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
                dispatch(updateFormField({ field: "sector", value: "" }));
              }}
            >
              {Array.isArray(industries) &&
                industries.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
            </TextField>
          )}
        />

        {formData.industry && (
          <Controller
            name="sector"
            control={control}
            rules={basicDetailsValidation.sector}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Sector"
                required
                error={!!errors.sector}
                helperText={errors.sector?.message}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                  },
                  mb: 2,
                }}
                value={formData?.sector || ""}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e);
                }}
              >
                {Array.isArray(sectors) &&
                  sectors.map((sector) => (
                    <MenuItem key={sector} value={sector}>
                      {sector}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
        )}
        {formData.sector === "Other (Pls Specify)" && (
          <Controller
            name="otherSector"
            control={control}
            rules={basicDetailsValidation.otherSector}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Enter Sector Name"
                required
                error={!!errors.otherSector}
                helperText={errors.otherSector?.message}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                  },
                  mb: 2,
                }}
                value={formData?.otherSector || ""}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e);
                }}
              ></TextField>
            )}
          />
        )}

        <Controller
          name="zipCode"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Zip Code"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              slotProps={{
                htmlInput: { maxLength: 10 },
                inputLabel: { shrink: true },
              }}
              value={formData?.zipCode || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="City"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              value={formData?.city || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />
        <Controller
          name="state"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="State"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              value={formData?.state || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        /> 
      </Box>

      <Box sx={{ mb: 4 }}>
        <Controller
          name="confirmations"
          control={control}
          rules={{ required: "Please confirm all statements to proceed" }}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value || formData.confirmations}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    handleChange({
                      target: {
                        name: "confirmations",
                        value: e.target.checked,
                      },
                    });
                  }}
                />
              }
              label={
                <>
                  I confirm that: <span style={{ color: "red" }}>*</span>
                </>
              }
              error={!!errors.confirmations}
            />
          )}
        />

        <Box sx={{ pl: 4 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • I/We, declare that we have a clean and satisfactory credit history with no history of default or significant delay in repayment of financial obligations.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • I am not classified as a Politically Exposed Person (PEP).
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • I have reviewed and understood the{" "}
            <Link
              component="button"
              onClick={(e) => {
                e.preventDefault();
                handleOpenTerms("masterAgreement");
              }}
              sx={{ textTransform: "none" }}
            >
              Terms & Condition
            </Link>
            ,{" "}
            {/* <Link onClick={handleOpenTermsOfService}>Terms & Condition</Link>,{" "} */}
            <Link onClick={handleOpenTermsOfService}>Terms of Service</Link>,
            and <Link onClick={handleOpenPrivacyPolicy}>Privacy Policy</Link>.
            By proceeding with the registration, I agree to adhere to and accept
            the terms outlined.
          </Typography>
          <Typography variant="body2">
            • Additionally, by providing the above information, I withdraw my
            NDNC registration and consent to Dequitec or its authorized
            representatives contacting me via SMS, call, email, or WhatsApp for
            any assistance, without causing me any inconvenience.
          </Typography>
        </Box>
        {errors?.confirmations && (
          <Typography
            variant="caption"
            color="error"
            sx={{ pl: 4, display: "block", mt: 1 }}
          >
            {errors?.confirmations?.message}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "#F5F9FF",
          p: 3,
          borderRadius: 1,
          mb: 3,
        }}
      >
        <ShieldIcon sx={{ color: "#4CAF50", mr: 2 }} />
        <Box>
          <Typography variant="body2">
            Your data is as secure as it gets. We use 256-bit encryption and
            follow ISO 27001 standards to protect your information.
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1, color: "text.secondary" }}
          >
            Your data is auto-saved to prevent any loss.
          </Typography>
        </Box>
      </Box>
      {/* {showCompanySelector && (
        <CompanySelectorDialog
          open={showCompanySelector}
          onClose={() => setShowCompanySelector(false)}
          onConfirm={(value) => {
            dispatch(updateFormField({ field: "entityType", value }));
          }}
        />
      )} */}

      <TermsDialog
        open={termsDialog.open}
        onClose={handleCloseTerms}
        title={termsDialog.title}
        content={termsDialog.content}
        highlightSection={termsDialog.highlightSection}
        showAgreeButton={!formData.confirmations}
        onAgree={handleAgreeTerms}
      />
    </Box>
  );
});

export default BasicDetailsForm;
